import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { allowedUploadExtensions } from '@core/constants';
import { DocumentExt, IDocumentResponseDTO } from '@core/model';
import { DateUtilities } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
// Ongoing effort to decide whether or not to keep DocumentsService under the core vs shared.
// For now will keep with the understanding that the scope of this file are just shared utility
// methods used by all streams with no http client calls.
// Note: Reason why we are not moving this to shared is because we haven't established a consistent way of
// instantiating shared services. All shared services are extended by stream specific context services and are then
// injected in the components, which in the scope of these methods is not necessary as they are not stream specific.
export class DocumentsService {
  private readonly CombineDocTypes = ['Upload','Inbound Faxes'];

  previewFileFaxDocs(file: DocumentExt): void {
    const hasValidExtension = allowedUploadExtensions.some(ext => file.fileName.toLowerCase().endsWith(ext));
    if (!hasValidExtension) {
      this.downloadFile(file);
      return;
    }

    const blob = this.b64toBlob(
      file.fileContent,
      this.getContentType(file.fileType.toLowerCase())
    );
    const url = URL.createObjectURL(blob);
    const w = window.open(url, file.fileName);

    if (w) {
      w.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url);
      });
    }
  }

  getfileSize(file: DocumentExt){
    const blob = this.b64toBlob(
      file.fileContent,
      this.getContentType(file.fileType.toLowerCase())
    );
    return blob.size;
  }

  downloadFile(file: DocumentExt): void {
    const blob = this.b64toBlob(
      file.fileContent,
      this.getContentType(file.fileType.toLowerCase())
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  getUniqueFilterOptions(types: any[]): SelectItem[] {
    let distinctTypesSet: Set<string | number>;
    let distinctTypes: (string | number)[];

    if (types.every(x => typeof x === 'number')) {
      distinctTypesSet = new Set(types as number[]);
      distinctTypes = Array.from(distinctTypesSet);
      distinctTypes.sort((a: number, b: number) => a - b);
    } else {
      distinctTypesSet = new Set(types as string[]);
      distinctTypes = Array.from(distinctTypesSet);
      distinctTypes.sort();
    }

    return distinctTypes.map(x => ({ value: x, label: x?.toString() }));
  }

  // Still used in AttachmentsSharedComponent, once refactored out of that, this can be removed.
  formatForTableDisplay(data: IDocumentResponseDTO[]): IDocumentResponseDTO[] {
    const combinedUploadedDocs: IDocumentResponseDTO[] = [];
    // Split on our sources, due to display need for DocumentType (see next comment)
    const uploadedDocs = data.filter(doc => this.CombineDocTypes.includes(doc.documentSource));
    const otherDocs = data.filter(doc => !this.CombineDocTypes.includes(doc.documentSource));

    const filteredFilesByName = otherDocs.filter((v, i) => otherDocs.findIndex(item => item.documentFileName === v.documentFileName) === i)
      .map(x => { return {...x, dateTimeFaxReceived: this.utcToLocal(x.createdOn)} });

    // This forEach combines the DocumentTypes for the same upload. Each type is saved separately, but needs to be recombined for Display.
    uploadedDocs.forEach(doc => {
      if (combinedUploadedDocs.some(d => d.documentFileName.toLowerCase() === doc.documentFileName.toLowerCase())) {
        return;
      }

      const tempFileArray = uploadedDocs.filter(d => d.documentFileName.toLowerCase() === doc.documentFileName.toLowerCase());
      const tmpDoc = { ...tempFileArray[0] };
      tmpDoc.documentType = tempFileArray.map(({ documentType }) => documentType).join(', ');

      tmpDoc.dateTimeFaxReceived = this.utcToLocal(tmpDoc.dateTimeFaxReceived);
      combinedUploadedDocs.push(tmpDoc);
    });

    const combinedDocs = [...filteredFilesByName, ...combinedUploadedDocs]

    // Sorting applied here due to the split we performed above
    return combinedDocs.sort((a, b) => new Date(b.dateTimeFaxReceived ?? b.createdOn).getTime() - new Date(a.dateTimeFaxReceived ?? a.createdOn).getTime());
  }

  utcToLocal(dateStr: string): string {
    if (!dateStr) return '';
    const isUTC = dateStr.endsWith('Z');
    const localDateStr = isUTC
      ? DateUtilities.utcToLocal(dateStr)
      : DateUtilities.convertUtcToLocal(dateStr);

    return formatDate(localDateStr, 'MM/dd/yyyy hh:mm:ss a', 'en-US');
  }

  private b64toBlob = (b64Data: string, contentType = '', chunkSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += chunkSize) {
      const slice = byteCharacters.slice(offset, offset + chunkSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  getContentType(fileType: string): string {
    const imageTypes = ['png', 'jpg', 'jpeg', 'gif'];

    if (imageTypes.includes(fileType)) {
      return `image/${fileType}`;
    } else {
      return `application/${fileType}`;
    }
  }

}
